import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SimplePageLayout } from "../../components/simplePage/simplePageLayout.tsx";
import { DataGrid, type DataGridHandle } from "../../components/dataGrid/dataGrid.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { useSession } from "../../hooks/useSession.ts";
import {
  generatePromotionCodes,
  getPromotionCode,
  savePromotionCode,
  emptyPromotionCode,
  type GenerateCodesArgs,
  type PromotionCode,
} from "../../api/promotionCodes.ts";
import { QrCode } from "../../util/qrcodegen.ts";
import styles from "../../styles/formPage.module.scss";

/* ── helpers ──────────────────────────────────────────────── */

interface PromotionOption {
  PromotionID: number;
  Title: string;
}

interface Status { type: "success" | "error"; message: string }

/**
 * Wall-clock helpers. The server's `dCodeFromDate` / `dCodeToDate` are .NET
 * `DateTime` values with `DateTimeKind.Unspecified` — they're serialized as
 * `"2024-01-15T09:00:00"` with NO timezone suffix. Treating them as either
 * UTC or browser-local on the round-trip shifts the saved time by the offset
 * between server and client, which is the bug the user hit ("set and save
 * time not same"). We therefore avoid `new Date(...)` entirely on these
 * values: format/parse as plain strings and let the server keep its own
 * (unspecified, wall-clock) semantics.
 */

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function todayLocalInputAt(hh: number, mm: number): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(hh)}:${pad2(mm)}`;
}

function threeMonthsFromNowAt(hh: number, mm: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(hh)}:${pad2(mm)}`;
}

/** Server ISO → `<input type="datetime-local">` value. No timezone math. */
function isoToLocalInput(iso: string): string {
  if (!iso) return "";
  // Strip any trailing Z / +hh:mm so the wall-clock the server stored is what
  // the input shows. `slice(0, 16)` yields "YYYY-MM-DDTHH:MM".
  const trimmed = iso.replace(/Z$/, "").replace(/[+-]\d{2}:?\d{2}$/, "");
  return trimmed.slice(0, 16);
}

/** `<input type="datetime-local">` value → wire format the server accepts. */
function localInputToWire(value: string): string {
  if (!value) return "";
  // Add ":00" seconds; never append Z, never convert to UTC.
  return value.length === 16 ? `${value}:00` : value;
}

/* ── Generate modal ─────────────────────────────────────── */

export function GenerateCodesModal({ promotions, onClose, onGenerated }: {
  promotions: PromotionOption[];
  onClose: () => void;
  onGenerated: () => void;
}) {
  const [promotionId, setPromotionId] = useState(0);
  const [codeTemplate, setCodeTemplate] = useState("");
  const [count, setCount] = useState(1);
  const [campaignName, setCampaignName] = useState("");
  const [isDrive, setIsDrive] = useState(false);
  const [validFrom, setValidFrom] = useState<string>(() => todayLocalInputAt(9, 0));
  const [validTo, setValidTo] = useState<string>(() => threeMonthsFromNowAt(23, 59));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);

  const handleGenerate = useCallback(async () => {
    if (!promotionId) { setStatus({ type: "error", message: "Please select a promotion." }); return; }
    if (!codeTemplate.trim()) { setStatus({ type: "error", message: "Code template is required." }); return; }
    const effectiveCampaign = isDrive ? "DRIVE" : campaignName.trim();
    if (!effectiveCampaign) { setStatus({ type: "error", message: "Campaign name is required." }); return; }
    if (!validFrom || !validTo) { setStatus({ type: "error", message: "Both dates are required." }); return; }
    if (validFrom >= validTo) { setStatus({ type: "error", message: "Valid From must be before Valid To." }); return; }

    setSaving(true);
    setStatus(null);
    try {
      const args: GenerateCodesArgs = {
        iPromotionID: promotionId,
        sCodeTemplate: codeTemplate.trim(),
        iCount: count,
        dCodeFromDate: localInputToWire(validFrom),
        dCodeToDate: localInputToWire(validTo),
        sCampaignName: effectiveCampaign,
      };
      const result = await generatePromotionCodes(args);
      const msg = count > 1
        ? `Generated ${count} codes. Generation group: ${result.iGenerationGroup}.`
        : `Code generated. ID: ${result.iPromotionCodeId}.`;
      setStatus({ type: "success", message: msg });
      onGenerated();
    } catch (err: unknown) {
      setStatus({ type: "error", message: String(err) });
    } finally {
      setSaving(false);
    }
  }, [promotionId, codeTemplate, count, campaignName, isDrive, validFrom, validTo, onGenerated]);

  return (
    <GridEditModal
      title="Generate Codes"
      saving={saving}
      statusMessage={status}
      saveLabel="Generate"
      savingLabel="Generating…"
      onClose={onClose}
      onSave={handleGenerate}
    >
      <div className={styles.fieldRowStacked}>
        <label>Promotion</label>
        <select className={styles.fieldSelect} value={promotionId} onChange={(e) => setPromotionId(Number(e.target.value))}>
          <option value={0}>-- Select promotion --</option>
          {promotions.map((p) => <option key={p.PromotionID} value={p.PromotionID}>{p.Title}</option>)}
        </select>
      </div>

      <div className={styles.fieldRowStacked}>
        <label>Code Template</label>
        <input className={styles.fieldInput} type="text" value={codeTemplate}
          onChange={(e) => setCodeTemplate(e.target.value)}
          placeholder="* = random letter, # = random digit" />
        <span className={styles.hint}>* = random letter, # = random digit, other chars literal</span>
      </div>

      <div className={styles.fieldRowStacked}>
        <label>Number of Codes</label>
        <input className={styles.fieldInput} type="number" value={count}
          onChange={(e) => setCount(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
          min={1} max={500} />
      </div>

      <label className={styles.checkLabel}>
        <input type="checkbox" checked={isDrive} onChange={(e) => setIsDrive(e.target.checked)} /> Is Drive
      </label>

      {!isDrive && (
        <div className={styles.fieldRowStacked}>
          <label>Campaign Name</label>
          <input className={styles.fieldInput} type="text" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} />
        </div>
      )}

      <div className={styles.fieldRowStacked}>
        <label>Valid From</label>
        <input className={styles.fieldInput} type="datetime-local" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
      </div>

      <div className={styles.fieldRowStacked}>
        <label>Valid To</label>
        <input className={styles.fieldInput} type="datetime-local" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
      </div>
    </GridEditModal>
  );
}

/* ── Edit modal ─────────────────────────────────────────── */

export function EditCodeModal({ codeId, promotions, onClose, onSaved, onOpenPromotion }: {
  codeId: number;
  promotions: PromotionOption[];
  onClose: () => void;
  onSaved: () => void;
  /** Optional navigation hook to swap this modal for the Promotion editor in
   *  the same popup slot (PagePopup.onNavigate). Renders the inline "Edit
   *  promotion" link next to the Promotion selector when provided. */
  onOpenPromotion?: (promotionId: number) => void;
}) {
  const [code, setCode] = useState<PromotionCode>(emptyPromotionCode);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);

  const [qrSize, setQrSize] = useState("200");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  // Dedupe by codeId — StrictMode double-mount would fire getPromotionCode twice.
  const lastLoadCodeIdRef = useRef<number | string | null>(null);
  useEffect(() => {
    if (lastLoadCodeIdRef.current === codeId) return;
    lastLoadCodeIdRef.current = codeId;
    setLoading(true);
    setStatus(null);
    setQrDataUrl(null);
    getPromotionCode(codeId)
      .then(setCode)
      .catch((err) => setStatus({ type: "error", message: String(err) }))
      .finally(() => setLoading(false));
  }, [codeId]);

  const set = <K extends keyof PromotionCode>(key: K, val: PromotionCode[K]) =>
    setCode((prev) => ({ ...prev, [key]: val }));

  const handleSave = useCallback(async () => {
    if (!code.Code.trim()) { setStatus({ type: "error", message: "Code is required." }); return; }

    setSaving(true);
    setStatus(null);
    try {
      const updated = await savePromotionCode(code);
      setCode(updated);
      setStatus({ type: "success", message: "Promotion code updated." });
      onSaved();
    } catch (err: unknown) {
      setStatus({ type: "error", message: String(err) });
    } finally {
      setSaving(false);
    }
  }, [code, onSaved]);

  const handleGenerateQR = () => {
    if (!code.Code) { setStatus({ type: "error", message: "No code to generate QR for." }); return; }
    try {
      const size = parseInt(qrSize, 10) || 200;
      const qr = QrCode.encodeText(code.Code, QrCode.Ecc.HIGH);
      const svg = qr.toSvgString(2);
      const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
      const svgUrl = URL.createObjectURL(blob);

      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, size, size);
        const pngUrl = canvas.toDataURL("image/png");
        setQrDataUrl(pngUrl);
        URL.revokeObjectURL(svgUrl);
      };
      img.src = svgUrl;
    } catch (err: unknown) {
      setStatus({ type: "error", message: `QR generation failed: ${String(err)}` });
    }
  };

  const fromLocal = isoToLocalInput(code.CodeFromDate);
  const toLocal = isoToLocalInput(code.CodeToDate);

  return (
    <GridEditModal
      title={`Edit Code #${codeId}`}
      wide
      loading={loading}
      saving={saving}
      statusMessage={status}
      onClose={onClose}
      onSave={handleSave}
    >
      <div className={styles.fieldRowStacked}>
        <label>ID</label>
        <input className={styles.fieldInput} type="text" value={code.PromotionCodeID || ""} readOnly />
      </div>

      <div className={styles.fieldRowStacked}>
        <label>
          Promotion
          {onOpenPromotion && code.PromotionID > 0 && (
            <button
              type="button"
              onClick={() => onOpenPromotion(code.PromotionID)}
              style={{
                marginLeft: "var(--space-2)",
                background: "none",
                border: "none",
                color: "var(--color-info)",
                textDecoration: "underline",
                cursor: "pointer",
                font: "inherit",
                padding: 0,
              }}
              title="Open the Promotion editor in this dialog"
            >
              edit promotion →
            </button>
          )}
        </label>
        <select
          className={styles.fieldSelect}
          value={code.PromotionID}
          onChange={(e) => set("PromotionID", Number(e.target.value))}
        >
          <option value={0}>-- Select --</option>
          {promotions.map((p) => (
            <option key={p.PromotionID} value={p.PromotionID}>{p.Title}</option>
          ))}
        </select>
      </div>

      <div className={styles.fieldRowStacked}>
        <label>Code</label>
        <input className={styles.fieldInput} type="text" value={code.Code} onChange={(e) => set("Code", e.target.value)} />
      </div>

      <fieldset style={{ border: "1px solid var(--color-border-default)", padding: "var(--space-2) var(--space-3)", borderRadius: "var(--radius-1)" }}>
        <legend style={{ fontWeight: "var(--font-semibold)", fontSize: "var(--text-sm)" }}>Valid Dates</legend>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)", alignItems: "center", marginBottom: "var(--space-2)" }}>
          <label style={{ width: 40 }}>From:</label>
          <input className={styles.fieldInput} type="datetime-local" value={fromLocal}
            onChange={(e) => set("CodeFromDate", localInputToWire(e.target.value))} style={{ flex: 1 }} />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)", alignItems: "center" }}>
          <label style={{ width: 40 }}>To:</label>
          <input className={styles.fieldInput} type="datetime-local" value={toLocal}
            onChange={(e) => set("CodeToDate", localInputToWire(e.target.value))} style={{ flex: 1 }} />
        </div>
      </fieldset>

      {/* QR Code section */}
      <div style={{ borderTop: "1px solid var(--color-border-default)", marginTop: "var(--space-4)", paddingTop: "var(--space-3)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)", alignItems: "flex-end", marginBottom: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: "1 1 100px" }}>
            <label>QR Size</label>
            <input className={styles.fieldInput} type="text" value={qrSize} onChange={(e) => setQrSize(e.target.value)} />
          </div>
          <button type="button" className={styles.secondaryBtn} onClick={handleGenerateQR}>Generate QR</button>
        </div>

        {qrDataUrl && (
          <div style={{ textAlign: "center" }}>
            <img src={qrDataUrl} alt="QR Code" style={{ maxWidth: "100%", imageRendering: "pixelated" }} />
            <p className={styles.hint} style={{ marginTop: "var(--space-2)" }}>Right-click the image and select "Save As" to download.</p>
          </div>
        )}
      </div>
    </GridEditModal>
  );
}

/* ── Page ─────────────────────────────────────────────── */

// Edit is opened via the `iPromotionCodeID` "Code ID" hyperlink (LinkID 7),
// which routes through PagePopup → EditCodeModal — NOT by clicking the row.
// So this page only owns the "generate" dialog; the grid handles edit itself.
type OpenDialog = { kind: "generate" } | null;

export function GenerateCodesPage() {
  const { cache } = useSession();
  const gridRef = useRef<DataGridHandle | null>(null);
  const [dialog, setDialog] = useState<OpenDialog>(null);

  const promotions = useMemo<PromotionOption[]>(() => {
    if (!cache?.Promotions) return [];
    return (cache.Promotions as Array<Record<string, unknown>>)
      .filter((p) => typeof p.PromotionID === "number" && typeof p.Title === "string")
      .map((p) => ({ PromotionID: p.PromotionID as number, Title: p.Title as string }))
      .sort((a, b) => a.Title.localeCompare(b.Title));
  }, [cache]);

  const close = useCallback(() => setDialog(null), []);
  const savedAndRefresh = useCallback(() => {
    setDialog(null);
    gridRef.current?.refresh();
  }, []);

  return (
    <SimplePageLayout title="Promotion Codes">
      <DataGrid
        ref={gridRef}
        name="promotionCodeSearch"
        toolbarExtra={
          <button className={styles.gridCreateBtn} onClick={() => setDialog({ kind: "generate" })}>+ Generate Codes</button>
        }
      />
      {dialog?.kind === "generate" && (
        <GenerateCodesModal
          promotions={promotions}
          onClose={close}
          onGenerated={savedAndRefresh}
        />
      )}
    </SimplePageLayout>
  );
}
